import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { api } from '../../lib/api'
import { normalizePhone, isValidNormalizedPhone } from '@velorent/shared'
import { useAuthStore } from '../../stores/auth'

type StaffRole = 'manager' | 'mechanic'

interface Manager {
  id: string
  name: string
  phone: string
  isActive: boolean
  lastLoginAt?: string
  role: 'owner' | 'manager' | 'mechanic'
  roles?: StaffRole[]
}

type EditForm = { name: string; phone: string; password: string }

function effectiveRoles(m: Pick<Manager, 'role' | 'roles'>): StaffRole[] {
  if (m.roles && m.roles.length > 0) return m.roles
  if (m.role === 'manager' || m.role === 'mechanic') return [m.role]
  return []
}

const ROLE_LABEL: Record<StaffRole, string> = { manager: 'Менеджер', mechanic: 'Механик' }
const ROLE_BADGE_BG: Record<StaffRole, string> = {
  manager: 'bg-blue-100 text-blue-700',
  mechanic: 'bg-amber-100 text-amber-700',
}

function apiError(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string }; message?: string } } }
  return e?.response?.data?.error?.message ?? e?.response?.data?.message ?? 'Ошибка при сохранении'
}

export default function ManagersPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { user, accessToken, refreshToken, startImpersonation } = useAuthStore()
  const [showForm, setShowForm] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createRoles, setCreateRoles] = useState<StaffRole[]>(['manager'])
  const [editManager, setEditManager] = useState<Manager | null>(null)
  const [editRoles, setEditRoles] = useState<StaffRole[]>(['manager'])
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null)

  const nameRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const toggleCreateRole = (r: StaffRole) =>
    setCreateRoles((prev) => prev.includes(r) ? (prev.length > 1 ? prev.filter((x) => x !== r) : prev) : [...prev, r])
  const toggleEditRole = (r: StaffRole) =>
    setEditRoles((prev) => prev.includes(r) ? (prev.length > 1 ? prev.filter((x) => x !== r) : prev) : [...prev, r])

  const { data } = useQuery({ queryKey: ['managers'], queryFn: () => api.get('/users').then((r) => r.data) })
  const editForm = useForm<EditForm>()

  const create = useMutation({
    mutationFn: (d: { name: string; phone: string; password: string; roles: StaffRole[] }) => api.post('/users', d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['managers'] })
      setShowForm(false)
      setCreateError(null)
      setCreateRoles(['manager'])
      if (nameRef.current) nameRef.current.value = ''
      if (phoneRef.current) phoneRef.current.value = ''
      if (passwordRef.current) passwordRef.current.value = ''
    },
    onError: (err) => setCreateError(apiError(err)),
  })

  const update = useMutation({
    mutationFn: ({ id, ...d }: { id: string } & Partial<EditForm> & { roles?: StaffRole[] }) =>
      api.patch(`/users/${id}`, {
        ...(d.name && { name: d.name }),
        ...(d.phone && { phone: d.phone }),
        ...(d.password && { password: d.password }),
        ...(d.roles && d.roles.length > 0 ? { roles: d.roles } : {}),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['managers'] }); setEditManager(null) },
    onError: (err) => alert(apiError(err)),
  })

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/users/${id}/${isActive ? 'activate' : 'deactivate'}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['managers'] }),
    onError: (err) => alert(apiError(err)),
  })

  const openEdit = (m: Manager) => {
    editForm.reset({ name: m.name, phone: m.phone, password: '' })
    setEditRoles(effectiveRoles(m).length > 0 ? effectiveRoles(m) : ['manager'])
    setEditManager(m)
  }

  const handleImpersonate = async (m: Manager, asRole?: StaffRole) => {
    if (!user || !accessToken) return
    setImpersonatingId(m.id)
    try {
      const res = await api.post(`/auth/impersonate/${m.id}`, asRole ? { role: asRole } : {})
      const { accessToken: managerToken, user: managerUser } = res.data
      startImpersonation(
        managerUser,
        managerToken,
        { user, accessToken, refreshToken },
      )
      // Mechanic gets their own layout under /mechanic
      navigate(managerUser.role === 'mechanic' ? '/mechanic' : '/manager')
    } catch (err) {
      alert(apiError(err))
    } finally {
      setImpersonatingId(null)
    }
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    setCreateError(null)
    const name = nameRef.current?.value?.trim() ?? ''
    const rawPhone = phoneRef.current?.value?.trim() ?? ''
    const password = passwordRef.current?.value ?? ''
    if (!name) { setCreateError('Введите имя'); return }
    if (!rawPhone) { setCreateError('Введите телефон'); return }
    if (password.length < 6) { setCreateError('Пароль — минимум 6 символов'); return }

    const phone = normalizePhone(rawPhone)
    if (!isValidNormalizedPhone(phone)) {
      setCreateError(`Неверный формат телефона. Ожидается: +7XXXXXXXXXX`)
      return
    }
    if (phoneRef.current) phoneRef.current.value = phone
    if (createRoles.length === 0) {
      setCreateError('Выберите хотя бы одну роль')
      return
    }

    create.mutate({ name, phone, password, roles: createRoles })
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Менеджеры</h2>
        <button onClick={() => { setShowForm(!showForm); setCreateError(null) }} className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm font-medium">
          + Добавить
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4">
          {createError && <p className="mb-2 text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{createError}</p>}
          <form onSubmit={handleCreate} className="space-y-3" autoComplete="off">
            <input ref={nameRef} name="manager-name" autoComplete="off" placeholder="Имя сотрудника" className="w-full rounded-xl border-gray-300 text-sm" />
            <input ref={phoneRef} name="manager-phone" type="tel" autoComplete="off" placeholder="+7 900 000 00 00" className="w-full rounded-xl border-gray-300 text-sm" />
            <input ref={passwordRef} name="manager-password" type="password" autoComplete="new-password" placeholder="Пароль (минимум 6 символов)" className="w-full rounded-xl border-gray-300 text-sm" />
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-gray-700">Роли (минимум одна)</p>
              <div className="flex gap-2">
                {(['manager', 'mechanic'] as StaffRole[]).map((r) => {
                  const checked = createRoles.includes(r)
                  return (
                    <label key={r} className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-sm ${
                      checked ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600'
                    }`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleCreateRole(r)} className="rounded" />
                      {ROLE_LABEL[r]}
                    </label>
                  )
                })}
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={create.isPending} className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm disabled:opacity-60">{create.isPending ? 'Создание...' : 'Создать'}</button>
              <button type="button" onClick={() => { setShowForm(false); setCreateError(null) }} className="border border-gray-300 px-4 py-2 rounded-xl text-sm">Отмена</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {data?.managers?.map((m: Manager) => {
          const roles = effectiveRoles(m)
          const isMulti = roles.length > 1
          const singleRole: StaffRole | null = roles.length > 0 ? roles[0]! : null
          return (
            <div key={m.id} className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold">{m.name}</p>
                  {roles.map((r) => (
                    <span key={r} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ROLE_BADGE_BG[r]}`}>
                      {ROLE_LABEL[r]}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-gray-500">{m.phone}</p>
                {m.lastLoginAt && <p className="text-xs text-gray-400 mt-1">Последний вход: {new Date(m.lastLoginAt).toLocaleString('ru')}</p>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2 py-1 rounded-full ${m.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {m.isActive ? 'Активен' : 'Отключён'}
                </span>
                <button onClick={() => openEdit(m)} className="text-xs px-3 py-1 rounded-lg border border-brand-300 text-brand-600 hover:bg-brand-50">
                  Изменить
                </button>
                {m.isActive && !isMulti && singleRole && (
                  <button
                    onClick={() => handleImpersonate(m, singleRole)}
                    disabled={impersonatingId === m.id}
                    className="text-xs px-3 py-1 rounded-lg border border-orange-300 text-orange-600 hover:bg-orange-50 disabled:opacity-60"
                  >
                    {impersonatingId === m.id ? '...' : `Войти как ${ROLE_LABEL[singleRole].toLowerCase()}`}
                  </button>
                )}
                {m.isActive && isMulti && roles.map((r) => (
                  <button
                    key={r}
                    onClick={() => handleImpersonate(m, r)}
                    disabled={impersonatingId === m.id}
                    className="text-xs px-3 py-1 rounded-lg border border-orange-300 text-orange-600 hover:bg-orange-50 disabled:opacity-60"
                  >
                    {impersonatingId === m.id ? '...' : `Как ${ROLE_LABEL[r].toLowerCase()}`}
                  </button>
                ))}
                <button
                  onClick={() => toggle.mutate({ id: m.id, isActive: !m.isActive })}
                  className="text-xs px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50"
                >
                  {m.isActive ? 'Отключить' : 'Включить'}
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Edit modal */}
      {editManager && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end" onClick={() => setEditManager(null)}>
          <div className="bg-white w-full rounded-t-3xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base">Редактировать менеджера</h3>
              <button onClick={() => setEditManager(null)} className="text-gray-400 text-2xl leading-none">×</button>
            </div>
            <form onSubmit={editForm.handleSubmit((d) => {
              const normalized = normalizePhone(d.phone)
              if (!isValidNormalizedPhone(normalized)) { alert('Неверный формат телефона. Ожидается: +7XXXXXXXXXX'); return }
              if (editRoles.length === 0) { alert('Выберите хотя бы одну роль'); return }
              update.mutate({
                id: editManager.id, name: d.name, phone: normalized,
                ...(d.password && { password: d.password }),
                roles: editRoles,
              })
            })} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Имя</label>
                <input {...editForm.register('name', { required: true })} className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Телефон</label>
                <input {...editForm.register('phone', { required: true })} type="tel" className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Роли (минимум одна)</label>
                <div className="flex gap-2">
                  {(['manager', 'mechanic'] as StaffRole[]).map((r) => {
                    const checked = editRoles.includes(r)
                    return (
                      <label key={r} className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-sm ${
                        checked ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600'
                      }`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleEditRole(r)} className="rounded" />
                        {ROLE_LABEL[r]}
                      </label>
                    )
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Новый пароль (оставьте пустым, чтобы не менять)</label>
                <input {...editForm.register('password')} type="password" placeholder="Минимум 6 символов" className="w-full rounded-xl border-gray-300 text-sm border px-4 py-3" />
              </div>
              <button type="submit" disabled={update.isPending} className="w-full bg-brand-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
                {update.isPending ? 'Сохранение...' : 'Сохранить'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
